import type { MeasuredChallengeRules } from "../../../domain/challenge/measured-types.js";
import { parseHandicapIndex } from "../../../shared/handicap-input.js";
import type {
  CourseSnapshot,
  HandicapMode,
  RoundConditionTag,
  RoundFormat,
  RoundKind,
  RivalrySummary,
  RoundStakeSnapshot,
  RoundWeatherSnapshot,
  TeamId,
} from "../../../domain/round/round-types.js";
import type { CreateRoundCommandPayload } from "../../../domain/round/round-reducer.js";
import type { PageParticipant } from "../../../shared/contracts/product-pages.js";
import type { ManualCourseInput } from "../../api/course-api.js";
import type { SimulatorCatalogProvenance } from "../../../domain/simulator/simulator-types.js";
import {
  simulatorRules,
  simulatorSetupProblem,
} from "./simulator-setup-model.js";
import { wizardTime } from "./wizard-time.js";
import {
  buildV3SetupInput,
  usesV3Scoring,
  validatePlayingHandicaps,
} from "./wizard-v3-state.js";

export type HoleSet = "18" | "front" | "back" | "custom";

export type ScoringChoice =
  | "stableford"
  | "stroke-play"
  | "match-play"
  | "nassau"
  | "total-only"
  | "team-best-ball"
  | "team-scramble"
  | "team-alternate-shot"
  | "team-greensomes"
  | "team-double-duel"
  | "declared-winner"
  | "high-low";

export interface WizardPlayer {
  id: string;
  displayName: string;
  handicap: string;
  /** Agreed playing handicap for exactly the selected holes, separate from HI. */
  playingHandicap?: string;
  teeId?: string | null;
  team: TeamId;
  you: boolean;
}

export interface CourseChoice {
  kind: "tee" | "manual" | "simulator" | "captured";
  capturedSource?: CourseSnapshot["source"];
  /** Original nullable tee ID; the selection key may be local to a rematch draft. */
  capturedTeeId?: string | null;
  teeId: string | null;
  courseName: string;
  teeName: string;
  rating: number | null;
  slope: number | null;
  holes: CourseSnapshot["holes"] | null;
  courseId?: string;
  dataConfirmed?: boolean;
  catalog?: SimulatorCatalogProvenance;
}

/** The stake as typed in the wizard; amount and label stay raw strings. */
export interface StakeChoice {
  kind: RoundStakeSnapshot["kind"];
  amount: string;
  label: string;
}

export interface WizardState {
  challengePreset?: MeasuredChallengeRules;
  kind: RoundKind;
  rivalrySummary?: RivalrySummary;
  scoring: ScoringChoice;
  handicap: boolean;
  holeSet: HoleSet;
  customHoles: number;
  players: WizardPlayer[];
  course: CourseChoice | null;
  teeChoices?: CourseChoice[];
  playedAt: string;
  playedAtEdited?: boolean;
  /** Per-player condition tags (cart, warm-up, alcohol) keyed by player id. */
  conditions: Record<string, RoundConditionTag[]>;
  /** Round-level tags (rain, wind) applied to every player at start. */
  roundTags: RoundConditionTag[];
  stake: StakeChoice | null;
  weather: RoundWeatherSnapshot | null;
  metricUnit: string;
  lowerWins: boolean;
  simulator?: {
    system: string;
    gimmeMode: "" | "off" | "distance";
    distance: string;
    unit: "m" | "ft";
  };
}

export const scoringChips: Record<RoundKind, Array<[ScoringChoice, string]>> = {
  outdoor: [
    ["stableford", "Stableford"],
    ["stroke-play", "Stroke Play"],
    ["match-play", "Match Play"],
    ["nassau", "Nassau"],
    ["total-only", "Total Only"],
  ],
  simulator: [
    ["stableford", "Stableford"],
    ["stroke-play", "Stroke Play"],
    ["match-play", "Match Play"],
    ["nassau", "Nassau"],
    ["total-only", "Total Only"],
  ],
  team: [
    ["team-best-ball", "Best Ball"],
    ["team-scramble", "Scramble"],
    ["team-alternate-shot", "Alternate Shot"],
    ["team-greensomes", "Greensomes"],
    ["team-double-duel", "Double Duel"],
  ],
  "golf-challenge": [
    ["declared-winner", "Declared Winner"],
    ["high-low", "Measured Result"],
  ],
  "ggb-import": [["stroke-play", "Stroke Play"]],
  minigame: [["declared-winner", "Declared Winner"]],
  custom: [
    ["stroke-play", "Stroke Play"],
    ["declared-winner", "Declared Winner"],
    ["high-low", "Measured Result"],
  ],
};

export function initialWizardState(
  kind: RoundKind,
  self: PageParticipant | undefined,
): WizardState {
  return {
    kind,
    ...(kind === "simulator" ? { rivalrySummary: "simulator" as const } : {}),
    scoring:
      kind === "outdoor" || kind === "simulator"
        ? "stroke-play"
        : (scoringChips[kind][0]?.[0] ?? "stroke-play"),
    handicap: false,
    holeSet: "18",
    customHoles: 12,
    players: self
      ? [
          {
            id: self.id,
            displayName: self.displayName,
            handicap:
              self.handicapDisplay == null ? "" : String(self.handicapDisplay),
            team: "a",
            you: true,
          },
        ]
      : [],
    course: null,
    playedAt: localNow(),
    conditions: {},
    roundTags: [],
    stake: null,
    weather: null,
    metricUnit: "points",
    lowerWins: false,
  };
}

export function localNow(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function formatOf(choice: ScoringChoice): RoundFormat {
  // "Total only" is scored as one measured result (total strokes, lower wins).
  return choice === "total-only" ? "high-low" : choice;
}

export function defaultHoles(count = 18): CourseSnapshot["holes"] {
  return Array.from({ length: count }, (_, index) => ({
    number: index + 1,
    par: 4,
    strokeIndex: index + 1,
  }));
}

/** A manual course always has a tee; this names the one nobody typed. */
export const DEFAULT_MANUAL_TEE = "Standard";

/**
 * The full course of a manually added choice, before the hole set is
 * applied. The tee name is what was typed, or the default tee when blank:
 * rounds and saved courses require a named tee.
 */
export function manualCourseSnapshot(
  state: WizardState,
  choice: CourseChoice,
  identity: Pick<CourseSnapshot, "id" | "source">,
): CourseSnapshot {
  const count =
    state.holeSet === "custom" ? Math.max(1, state.customHoles) : 18;
  return {
    ...identity,
    name: choice.courseName.trim() || "Course",
    teeName: choice.teeName.trim() || DEFAULT_MANUAL_TEE,
    rating: choice.rating,
    slope: choice.slope,
    holes: choice.holes?.length ? choice.holes : defaultHoles(count),
  };
}

/** The saved-course request for a manually added course. */
export function manualCourseInput(course: CourseSnapshot): ManualCourseInput {
  return {
    name: course.name,
    countryCode: null,
    tee: {
      name: course.teeName,
      rating: course.rating,
      slope: course.slope,
      holes: course.holes,
    },
  };
}

/** Applies the hole set to a full course (front / back / custom). */
export function selectHoles(
  holes: CourseSnapshot["holes"],
  set: HoleSet,
  custom: number,
): CourseSnapshot["holes"] {
  if (set === "front") return holes.filter((hole) => hole.number <= 9);
  if (set === "back")
    return holes.filter((hole) => hole.number >= 10 && hole.number <= 18);
  if (set === "custom")
    return holes.slice(0, Math.max(1, Math.min(custom, holes.length)));
  return holes;
}

/**
 * The course as played for the hole set: the chosen holes with the tee's
 * 18-hole rating scaled to their share, so the rating is compared with the
 * par of the holes actually played (slope is per stroke and stays).
 */
export function courseForHoleSet(
  course: CourseSnapshot,
  set: HoleSet,
  custom: number,
): CourseSnapshot {
  const holes = selectHoles(course.holes, set, custom);
  const share = course.holes.length ? holes.length / course.holes.length : 1;
  return {
    ...course,
    holes,
    rating:
      course.rating === null
        ? null
        : Math.round(course.rating * share * 10) / 10,
  };
}

/** The condition tags a player starts the round with (own + round-level). */
export function playerTags(
  state: WizardState,
  playerId: string,
): RoundConditionTag[] {
  const own = state.conditions[playerId] ?? [];
  return Array.from(new Set([...own, ...state.roundTags]));
}

/** The stake snapshot of a wizard choice (amount and label normalised). */
export function stakeSnapshot(stake: StakeChoice): RoundStakeSnapshot {
  const amount = Number.parseFloat(stake.amount.replace(",", "."));
  const label = stake.label.trim().slice(0, 80);
  return {
    kind: stake.kind,
    amount: stake.kind === "money" && Number.isFinite(amount) ? amount : null,
    label: stake.kind === "custom" && label ? label : null,
  };
}

export function validateWizard(state: WizardState): string | null {
  if (state.course?.kind === "captured" && !state.course.dataConfirmed)
    return "Confirm the previous tee data before starting.";
  const simulatorProblem = simulatorSetupProblem(state);
  if (simulatorProblem) return simulatorProblem;
  if (state.players.length < 1) return "Add at least one player.";
  if (state.players.length > 4) return "A round holds up to four players.";
  if (state.kind === "team") {
    if (state.players.length !== 4) return "Team play needs four players.";
    const teamA = state.players.filter((player) => player.team === "a").length;
    if (teamA !== 2) return "Put two players on each team.";
  }
  if (!state.course) return "Choose a course and tee.";
  if (usesV3Scoring(state)) {
    if (state.scoring === "match-play" && state.players.length !== 2)
      return "Match Play needs two players. Choose Stroke Play or Stableford for this flight.";
    return validatePlayingHandicaps(state);
  }
  return null;
}

export function buildSetupInput(
  state: WizardState,
  course: CourseSnapshot,
  playerCourses?: Record<string, CourseSnapshot>,
): CreateRoundCommandPayload {
  if (usesV3Scoring(state))
    return buildV3SetupInput(state, course, playerCourses);
  const totalOnly = state.scoring === "total-only";
  const rated = course.rating !== null && course.slope !== null;
  if (state.handicap && !rated)
    throw new Error(
      "Handicap scoring needs a rated tee for this format. Add its rating and slope, or choose gross scoring.",
    );
  const handicapMode: HandicapMode = state.handicap ? "full" : "off";
  const self = state.players.find((player) => player.you);
  return {
    participants: state.players.map((player) => ({
      id: player.id,
      displayName: player.displayName,
      handicapIndex: parseHandicap(player.handicap),
      teamId: state.kind === "team" ? player.team : null,
      tags: playerTags(state, player.id),
    })),
    course,
    conditions: {
      ...wizardTime(state),
      // Legacy flag: whether the scorekeeper rides a cart.
      cart: self ? playerTags(state, self.id).includes("cart") : false,
      weather: state.weather,
    },
    rules: {
      kind: state.kind,
      ...(state.kind === "simulator"
        ? {
            rivalrySummary: state.rivalrySummary ?? "simulator",
            simulator: simulatorRules(state),
          }
        : {}),
      format: formatOf(state.scoring),
      handicapMode,
      metric: totalOnly
        ? { unit: "strokes", lowerWins: true }
        : state.scoring === "high-low"
          ? { unit: state.metricUnit || "points", lowerWins: state.lowerWins }
          : null,
      stake: state.stake ? stakeSnapshot(state.stake) : null,
    },
  };
}

function parseHandicap(value: string): number | null {
  const parsed = parseHandicapIndex(value);
  if (value.trim() && parsed === null)
    throw new Error("Use a handicap from -10.0 to 54.0, with a comma or dot.");
  return parsed;
}
